import { CurrencyPipe, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { AuthService } from '@app/core/auth';
import { environment } from '@app/env-service';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

@Injectable({
  providedIn: 'root',
})
export class ClientUsersService {
  url :string = environment.url;

  curPipe :CurrencyPipe;
  datePipe :DatePipe;

  private readonly _users = new BehaviorSubject<any>(<any>[]);
  public users$ = this._users.asObservable();

  public gettingRecords :boolean = false;

  private params :any = {
    per_page: 20,
    page: 1,
  };

  constructor(private http :HttpClient) {}

  get(params) {
    this.gettingRecords = true;

    return this.http
      .get(`${this.url}/client-users`, { params: params })
      .subscribe((resp :any) => {
        this.gettingRecords = false;
        this._users.next(resp);
      });
  }

  refreshList() {
    this.get(this.params);
  }

  setParams(params :any) {
    this.params = params;
  }

  createUser(userData :any) :Observable<any> {
    return this.http.post(`${this.url}/client-users`, userData).pipe(
      tap((resp :any) => {
        setTimeout(() => {
          const users :any = this._users.getValue();
          if (users && users.data) {
            users.data.splice(0, 0, resp);
            this._users.next(users);
          }
        });
      }),
    );
  }

  updateRecord(data :any, id :number) :Observable<any> {
    return this.http
      .put(`${this.url}/client-users/${id}/reset-password`, data)
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  updateUser(userId :number, userData :any) :Observable<any> {
    return this.http.put(`${this.url}/client-users/${userId}`, userData).pipe(
      tap((resp :any) => {
        this.tryUpdateOrAddResp(resp);
      }),
    );
  }

  deleteUser(userId :number) {
    return this.http.delete(`${this.url}/client-users/${userId}`).pipe(
      tap((resp :any) => {
        const users :any = this._users.getValue();
        const poIdx = users.data.findIndex((row) => row.id == userId);
        users.data.splice(poIdx, 1);
        this._users.next(users);
      }),
    );
  }

  tryUpdateOrAddResp(resp) {
    const users :any = this._users.getValue();
    if (!users || !users.data) {
      return;
    }
    const userIdx = users.data.findIndex((row) => row.id == resp.id);

    users.data[userIdx] = resp;

    this._users.next(users);
  }

  search(params :any) :Observable<any> {
    return this.http.get(`${this.url}/client-users/search`, { params: params });
  }

  searchClientUserWithPaymentProfile(params :any) :Observable<any> {
    return this.http.get(
      `${this.url}/client-users/searchClientUserWithPaymentProfile`,
      { params: params },
    );
  }

  getDefaultClientUserWithPaymentProfile = (
    businessAccountId :number,
  ) :Observable<any> => {
    const params = { businessAccountId: businessAccountId };

    return this.http
      .get<any>(`${this.url}/client-users/getClientUserWithPaymentProfile`, {
        params: params,
      })
      .pipe(
        catchError(() => of([])),
        map((resp) => resp.data),
      );
  };

  searchClientUser(params :any) :Observable<any> {
    return this.http.get(`${this.url}/client-users/searchClientUser`, {
      params: params,
    });
  }

  getDefaultClientUser = (businessAccountId :number) :Observable<any> => {
    const params = { businessAccountId: businessAccountId };

    return this.http
      .get<any>(`${this.url}/client-users/getClientUser`, { params: params })
      .pipe(
        catchError(() => of([])),
        map((resp) => resp.data),
      );
  };

  checkEmailOfClientUsersIfExist(email :string) :Observable<any> {
    const params = { email: email };

    return this.http.get(
      `${this.url}/client-users/checkEmailOfClientUsersIfExist`,
      { params: params },
    );
  }
}
